import React, { useState, useEffect } from 'react';
import { FaSearch, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../security/security/AuthContext';

const InvestmentManagement = () => {
  const [investments, setInvestments] = useState([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(300);
  const { token } = useAuth();

  
  useEffect(() => {
    fetchInvestments();
  }, [currentPage, searchEmail]);

  const fetchInvestments = async () => {
    try {
      setLoading(true);
      const url = searchEmail
        ? `https://api.globallequityfinance.com/api/admin/investments/search?email=${searchEmail}`
        : 'https://api.globallequityfinance.com/api/admin/investments';
      
      const response = await axios.get(url, {
        headers: { Authorization: token }
      });
      
      // Sort investments by ID in descending order
      const sortedInvestments = [...response.data].sort((a, b) => b.id - a.id);
      setInvestments(sortedInvestments);
      setError(null);
    } catch (err) {
      setError('Failed to fetch investments');
      console.error('Error fetching investments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvestments();
  };

  const currentInvestments = investments.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="investment-management">
      <div className="investment-header">
        <h2>Investment Management</h2>
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-container">
            <input
              type="text"
              placeholder="Search by user email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              <FaSearch />
            </button>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="investment-table-container">
          <table className="investment-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Product Type</th>
                <th>User Email</th>
                <th>Invested Amount</th>
                <th>Daily Income</th>
                <th>Total Income</th>
                <th>Earned Amount</th>
                <th>Purchase Date</th>
                <th>Valid Until</th>
                <th>Status</th>
                <th>Ready to Receive</th>
              </tr>
            </thead>
            <tbody>
              {currentInvestments.map((investment) => (
                <tr key={investment.id}>
                  <td>{investment.productName}</td>
                  <td>{investment.productType}</td>
                  <td>{investment.userEmail}</td>
                  <td>KSH {investment.investedAmount?.toLocaleString()}</td>
                  <td>KSH {investment.dailyIncome?.toLocaleString()}</td>
                  <td>KSH {investment.totalIncome?.toLocaleString()}</td>
                  <td>KSH {investment.earnedAmount?.toLocaleString()}</td>
                  <td>{format(parseISO(investment.purchaseDate), 'PPp')}</td>
                  <td>{format(parseISO(investment.validUntil), 'PPp')}</td>
                  <td>
                    <span className={`status-badge ${investment.active ? 'active' : 'inactive'}`}>
                      {investment.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className={`ready-badge ${investment.readyToReceive ? 'ready' : 'not-ready'}`}>
                      {investment.readyToReceive ? <FaCheck /> : <FaTimes />}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="pagination-button"
            >
              Previous
            </button>
            <span className="page-number">Page {currentPage + 1}</span>
            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={currentInvestments.length < itemsPerPage}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentManagement;
